import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OverlayModal from '../../../components/ui/OverlayModal';
import type { CriminalCoverNoteResponse } from '../../../types/criminalApi';
import * as criminalService from '../../../services/criminalCaseRequests';
import { setFlashToast } from '../../../utils/flashToast';
import { formatDisplayDate, formatDisplayDateTime } from '../../../utils/dateFormat';
import { printCriminalCoverNotePdf } from '../../../utils/criminalCoverNotePrint';
import { coverNoteQrImageSrc } from '../../../utils/coverNoteQr';
import {
  criminalCoverNoteDivisionUnit,
  criminalCoverNoteSuspectsDisplay,
} from '../../../utils/criminalCoverNoteDisplay';

type CriminalCoverNoteModalProps = {
  isOpen: boolean;
  caseRequestId: string;
  fileRefFallback?: string;
  coverNoteFallback?: Partial<CriminalCoverNoteResponse>;
  showSuccessBanner?: boolean;
  onClose: () => void;
};

function displayValue(value: string | null | undefined): string {
  const t = value?.trim();
  return t || '—';
}

function CoverNoteBody({
  coverNote,
  qrSrc,
}: {
  coverNote: CriminalCoverNoteResponse;
  qrSrc: string;
}) {
  return (
    <div className="cn-body">
      <div className="cn-meta cn-meta-qf">
        <div className="cn-qr-box">
          {qrSrc ? (
            <img src={qrSrc} alt="Cover note QR code" width={68} height={68} style={{ display: 'block' }} />
          ) : (
            <div style={{ width: 68, height: 68, background: 'var(--bg)' }} aria-hidden />
          )}
          <div className="cn-qr-note">QR code for file intake, tracking and cover note verification.</div>
        </div>
        <div className="cn-box">
          <label>File Reference Number</label>
          <p>{displayValue(coverNote.fileReferenceNumber)}</p>
        </div>
        <div className="cn-box">
          <label>Request Submitted at</label>
          <p>{formatDisplayDateTime(coverNote.requestSubmittedAt) || '—'}</p>
        </div>
        <div className="cn-box">
          <label>Division / Unit</label>
          <p>
            {displayValue(
              criminalCoverNoteDivisionUnit(coverNote.submittedDivisionName, coverNote.submittedAgency),
            )}
          </p>
        </div>
        <div className="cn-box">
          <label>File Category</label>
          <p className="cat-val">{displayValue(coverNote.fileCategory)}</p>
          {coverNote.subFileCategory?.trim() ? (
            <p className="sub">Sub File Category: {coverNote.subFileCategory.trim()}</p>
          ) : null}
        </div>
      </div>

      <div className="cn-sum-title">Summary of Request Details</div>
      <div className="cn-sum-grid">
        <div className="cn-col">
          <div className="csf">
            <label>Case Source</label>
            <p>{displayValue(coverNote.caseSource)}</p>
          </div>
          <div className="csf">
            <label>Connected AGD File Reference Number</label>
            <p>{displayValue(coverNote.connectedAgdFileReferenceNumber)}</p>
          </div>
          <div className="csf">
            <label>Accused/ Suspect Name</label>
            <p>
              {displayValue(
                criminalCoverNoteSuspectsDisplay(
                  coverNote.suspects,
                  coverNote.accusedOrSuspectName,
                  coverNote.accusedOrSuspectStatus,
                ),
              )}
            </p>
          </div>
          <div className="csf">
            <label>Applicable Area(s)</label>
            <p>{displayValue(coverNote.applicableAreas)}</p>
          </div>
          <div className="csf">
            <label>Description</label>
            <p>{displayValue(coverNote.description)}</p>
          </div>
        </div>
        <div className="cn-col">
          <div className="csf">
            <label>Urgency Level</label>
            <p>{displayValue(coverNote.urgencyLevel)}</p>
          </div>
          <div className="csf">
            <label>Deadline</label>
            <p>{formatDisplayDate(coverNote.deadline) || '—'}</p>
          </div>
          <div className="csf">
            <label>Urgency Justification</label>
            <p>{displayValue(coverNote.urgencyJustification)}</p>
          </div>
          <div className="csf">
            <label>Documents Declared</label>
            <div className="dtags">
              {(coverNote.documents?.length ? coverNote.documents : ['None']).map((doc, index) => (
                <div key={`${doc}-${index}`} className="dtag">
                  {doc !== 'None' ? (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : null}
                  {doc}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

type CriminalCoverNoteModalContentProps = {
  caseRequestId: string;
  fileRefFallback?: string;
  coverNoteFallback?: Partial<CriminalCoverNoteResponse>;
  onFileRefReady?: (fileRef: string) => void;
  onClose: () => void;
};

function CriminalCoverNoteModalContent({
  caseRequestId,
  fileRefFallback,
  coverNoteFallback,
  onFileRefReady,
  onClose,
}: CriminalCoverNoteModalContentProps) {
  const { t } = useTranslation();
  const id = caseRequestId.trim();
  const loadKey = `${id}|${coverNoteFallback?.submittedDivisionName ?? ''}|${coverNoteFallback?.fileReferenceNumber ?? ''}`;
  const [coverNote, setCoverNote] = useState<CriminalCoverNoteResponse | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    if (!id) return;

    let mounted = true;
    void criminalService.getCriminalCoverNote(id, { fallback: coverNoteFallback }).then((r) => {
      if (!mounted) return;
      setLoadedKey(loadKey);
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Could not load cover note.', variant: 'error' });
        return;
      }
      setCoverNote(r.data);
      const ref = r.data.fileReferenceNumber?.trim();
      if (ref) onFileRefReady?.(ref);
    });

    return () => {
      mounted = false;
    };
  }, [id, loadKey, coverNoteFallback, onFileRefReady]);

  const loading = Boolean(id) && loadedKey !== loadKey;
  const fileRef = coverNote?.fileReferenceNumber?.trim() || fileRefFallback?.trim() || '—';

  const downloadPdf = async () => {
    if (downloading || !coverNote || !id) return;

    setDownloading(true);
    try {
      const r = await criminalService.downloadCriminalCoverNotePdf(id);
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Cover note PDF could not be loaded.', variant: 'error' });
        return;
      }

      const pdfBlob =
        r.data.type === 'application/pdf' ? r.data : new Blob([r.data], { type: 'application/pdf' });
      const safeRef = fileRef.replace(/[^\w.-]+/g, '_');
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cover-Note-${safeRef}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const printCoverNote = async () => {
    if (!coverNote || printing || !id) return;
    setPrinting(true);
    try {
      const r = await printCriminalCoverNotePdf(id, fileRef);
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Cover note PDF could not be loaded.', variant: 'error' });
      }
    } finally {
      setPrinting(false);
    }
  };

  const qrSrc = coverNote ? coverNoteQrImageSrc(coverNote.qr) : '';

  if (!id) {
    return (
      <div className="cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.coverNoteUnavailable')}
          <div style={{ marginTop: 16 }}>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.loadingCoverNote')}
        </div>
      </div>
    );
  }

  if (!coverNote) {
    return (
      <div className="cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <div className="cn-body" style={{ textAlign: 'center', color: 'var(--t3)', padding: '40px 20px' }}>
          {t('caseRequests.coverNoteUnavailable')}
          <div style={{ marginTop: 16 }}>
            <button className="mfb mfb-dk" type="button" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="cn-printable cn-scroll">
        <div className="cn-hdr">{t('caseRequests.coverNote')}</div>
        <CoverNoteBody coverNote={coverNote} qrSrc={qrSrc} />
      </div>
      <div className="cn-footer">
        <button className="mfb" type="button" disabled={downloading} onClick={() => void downloadPdf()}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          {downloading ? 'Downloading…' : 'Download as PDF'}
        </button>
        <button className="mfb" type="button" disabled={printing} onClick={() => void printCoverNote()}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <polyline points="6 9 6 2 18 2 18 9" />
            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
            <rect x="6" y="14" width="12" height="8" />
          </svg>
          {printing ? 'Preparing…' : 'Print'}
        </button>
        <button className="mfb mfb-dk" type="button" onClick={onClose}>
          Close
        </button>
      </div>
    </>
  );
}

export default function CriminalCoverNoteModal({
  isOpen,
  caseRequestId,
  fileRefFallback,
  coverNoteFallback,
  showSuccessBanner = false,
  onClose,
}: CriminalCoverNoteModalProps) {
  const [successFileRef, setSuccessFileRef] = useState(
    () => fileRefFallback?.trim() || coverNoteFallback?.fileReferenceNumber?.trim() || '—',
  );

  useEffect(() => {
    if (!isOpen) return;
    setSuccessFileRef(fileRefFallback?.trim() || coverNoteFallback?.fileReferenceNumber?.trim() || '—');
  }, [isOpen, fileRefFallback, coverNoteFallback]);

  return (
    <OverlayModal
      isOpen={isOpen}
      overlayClassName="overlay"
      contentClassName="cn-mbox cn-mbox--cover"
      centerVertically
      onClose={onClose}
    >
      {showSuccessBanner ? (
        <div className="success-bar cn-cover-success">
          <div className="success-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div>
            <div className="success-title">Submission successful</div>
            <div className="success-sub">
              Request reference <strong>{successFileRef}</strong> has been created and timestamped. Attach the printed Cover
              Note before handing over the physical case file to AGD.
            </div>
          </div>
        </div>
      ) : null}

      {isOpen ? (
        <CriminalCoverNoteModalContent
          key={caseRequestId}
          caseRequestId={caseRequestId}
          fileRefFallback={fileRefFallback}
          coverNoteFallback={coverNoteFallback}
          onFileRefReady={setSuccessFileRef}
          onClose={onClose}
        />
      ) : null}
    </OverlayModal>
  );
}

